import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { PortalHeader } from "@/components/PortalHeader";
import { PortalFooter } from "@/components/PortalFooter";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { NotificationBanner } from "@/components/NotificationBanner";
import { UploadCard } from "@/components/UploadCard";
import { PolicyBuilder } from "@/components/PolicyBuilder";
import { CandidateFieldEditCard } from "@/components/CandidateFieldEditCard";
import { FilePreviewDialog } from "@/components/FilePreviewDialog";
import { useRegistration } from "@/context/RegistrationContext";
import { syncPolicySignStatus, getCandidateToken, markNeedsOtpReauth, OTP_REAUTH_TIP } from "@/lib/api";
import { DOCUMENT_TYPES, POLICY_ITEMS, CANDIDATE_FIELD_ITEMS, registrationIdLabel } from "@/lib/constants";
import { downloadCandidatePdf } from "@/lib/pdfExport";
import {
  CheckCircle2, Clock, FileText, MapPin, Calendar, ArrowUpRight, ScrollText, Download, User as UserIcon, RotateCcw, AlertTriangle, Eye, Lock, XCircle, PauseCircle,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

const CircularProgress = ({ value }) => {
  const size = 140, stroke = 10;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (value / 100) * c;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        <circle cx={size/2} cy={size/2} r={r} stroke="#F3F4F6" strokeWidth={stroke} fill="none" />
        <motion.circle cx={size/2} cy={size/2} r={r} stroke="#B60F2A" strokeWidth={stroke} fill="none" strokeLinecap="round" strokeDasharray={c} initial={{ strokeDashoffset: c }} animate={{ strokeDashoffset: offset }} transition={{ duration: 0.8, ease: "easeOut" }} transform={`rotate(-90 ${size/2} ${size/2})`} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="text-2xl font-bold text-[#111]">{value}%</div>
        <div className="tiny-label">Complete</div>
      </div>
    </div>
  );
};

export default function CandidateDashboard() {
  const { state, setState, setDocuments, refreshDocStatus, hydrated } = useRegistration();
  const nav = useNavigate();
  const path = useLocation().pathname;
  const c = state.candidate || {};
  const [preview, setPreview] = useState(null); // { file, docLabel }

  // Once the application is verified, it's terminal — no edits of any kind,
  // even if a stray reupload flag is still sitting on the record from before
  // verification (e.g. an older admin action). Nothing is editable past this point.
  const isVerified = state.status === "verified";

  useEffect(() => {
    if (!hydrated) return;
    if (!state.submitted && !state.applicationNumber) nav("/", { replace: true });
  }, [hydrated, state.submitted, state.applicationNumber, nav]);

  useEffect(() => {
    if (getCandidateToken()) return;
    markNeedsOtpReauth();
    toast.info(OTP_REAUTH_TIP, { duration: 10000 });
    nav("/", { replace: true });
  }, [nav]);

  // Pick up admin actions (reupload flags, file deletions) without requiring a
  // hard page reload — refresh on entry, then poll while the dashboard is open.
  useEffect(() => {
    refreshDocStatus();
    const iv = setInterval(refreshDocStatus, 20000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.applicationNumber]);

  // Reconcile any policy still "pending" against Leegality directly — the
  // Leegality webhook can miss/fail (per-invitee delivery, signature
  // mismatches, network blips), and refreshDocStatus above only re-reads our
  // OWN stored state, it never asks Leegality itself. Without this, a policy
  // signed via the emailed link after the candidate has already left the
  // registration wizard's Policies step (where PolicyBuilder's own polling
  // lives) would show "pending" forever until an admin happens to open this
  // application and trigger a sync as a side effect of viewing it.
  useEffect(() => {
    if (!hydrated || !state.applicationNumber || state.skipPolicies) return undefined;
    let cancelled = false;
    const syncPending = async () => {
      const pendingIds = POLICY_ITEMS.filter((p) => !p.viewOnly).map((p) => p.id).filter((id) => {
        const meta = state.policies?.[id];
        return meta?.signStatus === "pending" || (!meta?.signStatus && meta?.sent);
      });
      for (const policyId of pendingIds) {
        const { ok, data } = await syncPolicySignStatus(state.applicationNumber, policyId);
        if (cancelled || !ok || data?.signStatus !== "signed") continue;
        setState((s) => ({
          ...s,
          policies: {
            ...s.policies,
            [policyId]: { ...(s.policies?.[policyId] || {}), sent: true, signStatus: "signed", signedAt: data.signedAt || new Date().toISOString() },
          },
        }));
      }
    };
    syncPending();
    const iv = setInterval(syncPending, 20000);
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrated, state.applicationNumber]);

  // The status card used to just check state.submitted, which stays true
  // forever once submitted — so verified/rejected/on_hold/withdrawn all
  // showed the same generic "Submitted" badge even though the real status
  // had moved on. Reflect the actual status instead. Withdrawn is checked
  // BEFORE the submitted branch — an admission-level withdrawal can happen
  // before the candidate ever submits, and used to fall through to the
  // generic "In Progress" label with no indication anything had happened.
  const statusDisplay =
    state.status === "withdrawn"
    ? { Icon: XCircle, color: "text-red-600", label: "Withdrawn", desc: state.lastActionReason || "This application has been withdrawn" }
    : !state.submitted
    ? { Icon: Clock, color: "text-amber-600", label: "In Progress", desc: "Complete all steps to submit" }
    : state.status === "verified"
    ? { Icon: CheckCircle2, color: "text-green-600", label: "Approved", desc: "Your registration is approved and locked" }
    : state.status === "rejected"
    ? { Icon: XCircle, color: "text-red-600", label: "Rejected", desc: state.lastActionReason || "Contact the Registrar Office for details" }
    : state.status === "on_hold"
    ? { Icon: PauseCircle, color: "text-slate-600", label: "On Hold", desc: "You were not available at your reporting slot" }
    : { Icon: Clock, color: "text-amber-600", label: "Submitted", desc: "Under review by admissions team" };

  const totalDocs = DOCUMENT_TYPES.length;
  const uploaded = DOCUMENT_TYPES.filter((d) => (state.documents[d.id] || []).length > 0).length;
  const progress = Math.round((uploaded / totalDocs) * 100);

  // Mandatory documents flagged for reupload get a dedicated, always-visible
  // alert (not just the Documents tab) — these block the application, so the
  // candidate shouldn't have to go looking for them. Nationality proof's
  // "required" flag is dynamic (only mandatory for non-Indian candidates),
  // same rule as the registration wizard.
  const isNonIndian = c.nationality && c.nationality !== "Indian";
  const mandatoryReuploadDocs = isVerified ? [] : DOCUMENT_TYPES.filter((d) => {
    const required = d.id === "nationality" ? isNonIndian : d.required;
    return required && state.docStatuses?.[d.id] === "reupload";
  });

  // Admin-requested corrections to name/parent names/photo — same "action needed"
  // treatment as mandatory documents.
  const flaggedFields = isVerified ? [] : CANDIDATE_FIELD_ITEMS.filter((f) => state.docStatuses?.[f.id] === "reupload");

  // Same value/change shape PolicyBuilder expects, scoped to dashboard state —
  // lets a flagged policy be re-signed and re-uploaded right here, without
  // sending the candidate back into the registration wizard for it.
  const policyValueFor = (policyId) => ({ ...(state.policies?.[policyId] || {}), filled: state.policiesFilled?.[policyId] || {} });
  const setPolicyOne = (policyId, val) => {
    const { filled, ...fileMeta } = val || {};
    setState((s) => ({
      ...s,
      policies: { ...(s.policies || {}), [policyId]: fileMeta },
      policiesFilled: { ...(s.policiesFilled || {}), [policyId]: filled || {} },
    }));
  };

  let sub = null;
  if (path.startsWith("/dashboard/registration")) {
    sub = (
      <div className="mu-card p-6" data-testid="section-registration">
        <div className="tiny-label">Registration</div>
        <h3 className="text-lg font-semibold text-[#111] mb-4">My Registration</h3>
        <dl className="divide-y divide-[#F3F4F6] text-sm">
          {[[registrationIdLabel(state.skipPolicies), state.applicationNumber], ["ACK Number", state.ackNumber], ["Roll Number", state.assignedSlot?.rollNumber], ["Candidate", c.name], ["Mobile", state.mobile], ["Email", c.email], ["Nationality", c.nationality], ["DOB", c.dateOfBirth], ["Address", [c.address, c.city, c.state, c.pincode].filter(Boolean).join(", ")], ["Parent 1 Name", c.parent1Name], ["Parent 2 Name", c.parent2Name], ["Reporting", state.assignedSlot ? `${state.assignedSlot.dateLabel} · ${state.assignedSlot.timeLabel}` : null]].map(([k, v]) => (
            <div key={k} className="grid grid-cols-1 sm:grid-cols-[220px_1fr] gap-1 py-2">
              <dt className="text-[#6B7280]">{k}</dt>
              <dd className="text-[#111] font-medium break-words">{v || "—"}</dd>
            </div>
          ))}
        </dl>
      </div>
    );
  } else if (path.startsWith("/dashboard/documents")) {
    const flaggedDocs = isVerified ? [] : DOCUMENT_TYPES.filter((d) => state.docStatuses?.[d.id] === "reupload");
    const flaggedIds = new Set(flaggedDocs.map((d) => d.id));
    const okDocs = DOCUMENT_TYPES.filter((d) => !flaggedIds.has(d.id));
    sub = (
      <div className="mu-card p-6" data-testid="section-documents">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="tiny-label">Documents</div>
            <h3 className="text-lg font-semibold text-[#111]">Uploaded Documents</h3>
          </div>
          {isVerified && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 ring-1 ring-inset ring-green-200 px-2.5 py-1 rounded-full">
              <Lock className="h-3 w-3" /> Verified — locked
            </span>
          )}
        </div>

        {(flaggedFields.length > 0 || flaggedDocs.length > 0) && (
          <div className="mb-6">
            <div className="tiny-label mb-2 text-orange-700">Action needed — reupload</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {flaggedFields.map((f) => (
                <CandidateFieldEditCard key={f.id} field={f} />
              ))}
              {flaggedDocs.map((d) => (
                <UploadCard
                  key={d.id}
                  doc={d}
                  files={state.documents[d.id] || []}
                  onChange={(next) => setDocuments(d.id, next)}
                />
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {okDocs.map((d) => {
            const files = state.documents[d.id] || [];
            const has = files.length > 0;
            return (
              <button
                key={d.id}
                type="button"
                onClick={() => has && setPreview({ file: files[0], docLabel: d.label })}
                disabled={!has}
                className={`flex items-center justify-between px-3 py-2.5 rounded-xl border border-[#E5E7EB] bg-[#F7F7F7]/60 text-left ${has ? "hover:border-[#B60F2A] hover:bg-white transition cursor-pointer" : "cursor-default"}`}
                data-testid={`dash-doc-${d.id}`}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-8 w-8 rounded-lg bg-white border border-[#E5E7EB] flex items-center justify-center shrink-0"><FileText className="h-3.5 w-3.5 text-[#B60F2A]" /></div>
                  <span className="text-sm text-[#111] truncate">{d.label}</span>
                </div>
                {has ? (
                  <span className="inline-flex items-center gap-1 text-[11px] font-medium text-green-700 bg-green-50 ring-1 ring-inset ring-green-200 px-2 py-0.5 rounded-full shrink-0">
                    <Eye className="h-3 w-3" /> {files.length > 1 ? `${files.length} files` : "View"}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 bg-amber-50 ring-1 ring-inset ring-amber-200 px-2 py-0.5 rounded-full shrink-0"><Clock className="h-3 w-3" /> Pending</span>
                )}
              </button>
            );
          })}
        </div>
        {!state.skipPolicies && (() => {
          const flaggedPolicies = isVerified ? [] : POLICY_ITEMS.filter((p) => state.docStatuses?.[p.id] === "reupload");
          const flaggedPolicyIds = new Set(flaggedPolicies.map((p) => p.id));
          const okPolicies = POLICY_ITEMS.filter((p) => !flaggedPolicyIds.has(p.id));
          return (
            <>
              {flaggedPolicies.length > 0 && (
                <div className="mt-6">
                  <div className="tiny-label mb-2 text-orange-700">Action needed — reupload</div>
                  <div className="grid grid-cols-1 gap-5">
                    {flaggedPolicies.map((p) => (
                      <PolicyBuilder
                        key={p.id}
                        item={p}
                        value={policyValueFor(p.id)}
                        onChange={(v) => setPolicyOne(p.id, v)}
                        state={state}
                      />
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-6">
                <div className="tiny-label mb-2">Policies</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {okPolicies.map((p) => {
                    const signStatus = state.policies?.[p.id]?.signStatus;
                    const isSigned = signStatus === "signed";
                    const isPending = signStatus === "pending";
                    return (
                      <div
                        key={p.id}
                        className="flex items-center justify-between px-3 py-2.5 rounded-xl border border-[#E5E7EB] bg-[#F7F7F7]/60"
                        data-testid={`dash-policy-${p.id}`}
                      >
                        <span className="text-sm text-[#111] truncate">{p.label}</span>
                        {isSigned ? <span className="inline-flex items-center gap-1 text-[11px] font-medium text-green-700 bg-green-50 ring-1 ring-inset ring-green-200 px-2 py-0.5 rounded-full shrink-0"><CheckCircle2 className="h-3 w-3" /> Signed</span> : isPending ? <span className="text-[11px] font-medium text-orange-700 bg-orange-50 ring-1 ring-inset ring-orange-200 px-2 py-0.5 rounded-full shrink-0">Signature Pending</span> : <span className="text-[11px] font-medium text-amber-700 bg-amber-50 ring-1 ring-inset ring-amber-200 px-2 py-0.5 rounded-full shrink-0">Pending</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          );
        })()}

        <FilePreviewDialog
          open={!!preview}
          onOpenChange={(v) => !v && setPreview(null)}
          file={preview?.file}
          docLabel={preview?.docLabel}
        />
      </div>
    );
  } else if (path.startsWith("/dashboard/ack")) {
    sub = (
      <div className="mu-card p-6 text-center" data-testid="section-ack">
        <div className="tiny-label">Registration Summary Slip</div>
        <h3 className="text-lg font-semibold text-[#111] mb-2">Download your Registration Summary</h3>
        {state.submitted ? (
          <>
            <p className="text-sm text-[#6B7280] mb-6 max-w-md mx-auto">Your personalised Registration Summary PDF with Roll Number and reporting slot details.</p>
            <button onClick={async () => { try { toast.loading("Generating…", { id: "pdf" }); await downloadCandidatePdf(state); toast.success("Downloaded", { id: "pdf" }); } catch { toast.error("Failed", { id: "pdf" }); } }} className="mu-btn-primary inline-flex">
              <Download className="h-4 w-4" /> Download Registration Summary Slip (PDF)
            </button>
          </>
        ) : (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 max-w-md mx-auto">
            Your ACK number, roll number and QR code are only assigned once you submit your registration. Complete and submit your application first.
          </p>
        )}
      </div>
    );
  } else if (path.startsWith("/dashboard/profile")) {
    sub = (
      <div className="mu-card p-6" data-testid="section-profile">
        <div className="flex items-center gap-4 mb-6">
          <div className="h-16 w-16 rounded-2xl overflow-hidden bg-[#F7F7F7] border border-[#E5E7EB]">
            {(c.photo?.file_url || c.photo?.dataUrl) ? <img src={c.photo.file_url || c.photo.dataUrl} alt={c.name} className="w-full h-full object-cover" /> : <UserIcon className="h-8 w-8 text-[#9CA3AF] m-4" />}
          </div>
          <div>
            <div className="text-lg font-semibold text-[#111]">{c.name || "—"}</div>
            <div className="text-xs text-[#6B7280]">{state.applicationNumber} · Roll {state.assignedSlot?.rollNumber || "—"}</div>
          </div>
        </div>
        <dl className="divide-y divide-[#F3F4F6] text-sm">
          {[["Email", c.email], ["Mobile", state.mobile], ["Gender", c.gender], ["Blood Group", c.bloodGroup], ["Parent 1", c.parent1Name], ["Parent 1 Phone", c.parent1Phone], ["Parent 2", c.parent2Name], ["Parent 2 Phone", c.parent2Phone], ["Emergency", `${c.emergencyContactName || "—"} (${c.emergencyContactRelation || "—"}) ${c.emergencyContactPhone || ""}`]].map(([k, v]) => (
            <div key={k} className="grid grid-cols-1 sm:grid-cols-[220px_1fr] gap-1 py-2">
              <dt className="text-[#6B7280]">{k}</dt>
              <dd className="text-[#111] font-medium break-words">{v || "—"}</dd>
            </div>
          ))}
        </dl>
      </div>
    );
  }

  if (state.applicationNumber && !hydrated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F7F7] text-[#6B7280] text-sm">
        Loading your registration…
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-[#F7F7F7]">
      <DashboardSidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <div className="lg:hidden"><PortalHeader /></div>
        <main className="flex-1 p-6 lg:p-10 max-w-7xl w-full">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <div className="tiny-label">Candidate Portal</div>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-[#111] mt-1">
                Welcome, {c.name?.split(" ")[0] || "Candidate"}
              </h1>
              <p className="text-[#6B7280] mt-1">Track your registration, documents and reporting details in one place.</p>
            </div>
            {state.submitted && (
              <Link to="/success" className="mu-btn-secondary" data-testid="btn-view-ack">
                <ScrollText className="h-4 w-4" /> View Registration Summary Slip
              </Link>
            )}
          </div>

          {state.applicationNumber && (
            <div className="mt-6"><NotificationBanner applicationNumber={state.applicationNumber} /></div>
          )}

          {(mandatoryReuploadDocs.length > 0 || flaggedFields.length > 0) && (
            <motion.div
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 mu-card p-4 md:p-5 border-red-300 bg-red-50 flex items-start gap-3"
              data-testid="mandatory-reupload-alert"
            >
              <div className="h-9 w-9 rounded-xl bg-red-100 flex items-center justify-center shrink-0">
                <AlertTriangle className="h-4 w-4 text-red-700" />
              </div>
              <div className="flex-1 min-w-0">
                {(() => {
                  const items = [...mandatoryReuploadDocs, ...flaggedFields];
                  return (
                    <div className="text-sm font-semibold text-red-800">
                      Action needed: {items.map((d) => d.label).join(", ")} {items.length > 1 ? "need" : "needs"} your attention
                    </div>
                  );
                })()}
                <div className="text-xs text-red-700 mt-1">
                  Your application can&rsquo;t be verified until these are fixed.
                </div>
                <button
                  onClick={() => nav("/dashboard/documents")}
                  className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-red-800 hover:underline"
                  data-testid="btn-fix-mandatory-reupload"
                >
                  <RotateCcw className="h-3 w-3" /> Reupload now
                </button>
              </div>
            </motion.div>
          )}

          {sub ? (
            <div className="mt-8">{sub}</div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-8">
                <div className="mu-card p-5" data-testid="card-application-status">
                  <div className="tiny-label">Application Status</div>
                  <div className="mt-2 flex items-center gap-2">
                    <statusDisplay.Icon className={`h-5 w-5 ${statusDisplay.color}`} />
                    <span className="font-semibold text-[#111]">{statusDisplay.label}</span>
                  </div>
                  <div className="text-xs text-[#6B7280] mt-2">{statusDisplay.desc}</div>
                </div>
                <div className="mu-card p-5" data-testid="card-ack-number">
                  <div className="tiny-label">Roll Number</div>
                  <div className="mt-2 font-semibold text-[#B60F2A] text-2xl tracking-wider">{state.assignedSlot?.rollNumber || "—"}</div>
                  <div className="text-xs text-[#6B7280] mt-2">ACK · {state.ackNumber || "Pending"}</div>
                </div>
                <div className="mu-card p-5" data-testid="card-reporting-slot">
                  <div className="tiny-label">{state.assignedSlot?.mode === "walkin" ? "Reporting Window" : "Reporting Slot"}</div>
                  <div className="mt-2 font-semibold text-[#111]">{state.assignedSlot?.dateLabel || (state.submitted ? "TBD" : "Not assigned")}</div>
                  <div className="text-xs text-[#6B7280] mt-1 flex items-center gap-1"><Calendar className="h-3 w-3" /> {state.assignedSlot?.timeLabel || "Assigned on submission"}</div>
                  <div className="text-xs text-[#6B7280] mt-1 flex items-center gap-1"><MapPin className="h-3 w-3" /> {state.assignedSlot?.venue || "School of Management Building"}</div>
                  {(state.assignedSlot?.mode === "token" || state.assignedSlot?.mode === "walkin") && (
                    <div className="text-xs text-[#B60F2A] mt-1">No fixed seat — token issued at check-in</div>
                  )}
                  {state.assignedSlot?.mode === "walkin" && state.assignedSlot?.excludedDatesLabel && (
                    <div className="text-xs text-amber-700 mt-1">Closed on: {state.assignedSlot.excludedDatesLabel}</div>
                  )}
                </div>
                <div className="mu-card p-5" data-testid="card-document-status">
                  <div className="tiny-label">Documents</div>
                  <div className="mt-2 font-semibold text-[#111]">{uploaded}/{totalDocs} uploaded</div>
                  <div className="mt-2 h-1.5 rounded-full bg-[#F3F4F6] overflow-hidden"><div className="h-full bg-[#B60F2A] transition-all" style={{ width: `${progress}%` }} /></div>
                  <div className="text-xs text-[#6B7280] mt-2">{progress}% of files ready</div>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6 mt-6">
                <div className="mu-card p-6 flex flex-col items-center justify-center">
                  <div className="tiny-label mb-2">Overall Progress</div>
                  <CircularProgress value={progress} />
                  <div className="text-xs text-[#6B7280] mt-4 text-center max-w-[200px]">Keep documents ready. Verified applications receive their reporting confirmation via email.</div>
                </div>

                <div className="mu-card p-6" data-testid="dashboard-doc-status">
                  <div className="flex items-center justify-between">
                    <div><div className="tiny-label">Document status</div><h3 className="text-lg font-semibold text-[#111]">Uploaded documents</h3></div>
                    {!state.submitted && <Link to="/register" className="text-sm text-[#B60F2A] hover:underline inline-flex items-center gap-1">Continue registration <ArrowUpRight className="h-3.5 w-3.5" /></Link>}
                  </div>
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {DOCUMENT_TYPES.map((d) => {
                      const has = (state.documents[d.id] || []).length > 0;
                      const needsReupload = !isVerified && state.docStatuses?.[d.id] === "reupload";
                      return (
                        <div key={d.id} className="flex items-center justify-between px-3 py-2.5 rounded-xl border border-[#E5E7EB] bg-[#F7F7F7]/60">
                          <div className="flex items-center gap-3 min-w-0"><div className="h-8 w-8 rounded-lg bg-white border border-[#E5E7EB] flex items-center justify-center"><FileText className="h-3.5 w-3.5 text-[#B60F2A]" /></div><span className="text-sm text-[#111] truncate">{d.label}</span></div>
                          {needsReupload ? (
                            <button onClick={() => nav("/dashboard/documents")} className="inline-flex items-center gap-1 text-[11px] font-semibold text-orange-800 bg-orange-100 ring-1 ring-inset ring-orange-300 px-2 py-0.5 rounded-full hover:bg-orange-200 transition">
                              <RotateCcw className="h-3 w-3" /> Reupload needed
                            </button>
                          ) : has ? <span className="inline-flex items-center gap-1 text-[11px] font-medium text-green-700 bg-green-50 ring-1 ring-inset ring-green-200 px-2 py-0.5 rounded-full"><CheckCircle2 className="h-3 w-3" /> Ready</span> : <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-700 bg-amber-50 ring-1 ring-inset ring-amber-200 px-2 py-0.5 rounded-full"><Clock className="h-3 w-3" /> Pending</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </>
          )}
        </main>
        <div className="lg:hidden"><PortalFooter /></div>
      </div>
    </div>
  );
}
